/**
 * The component showing the list of pending invitations.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam 2026
 */

import {
  DateType,
  UserPendingInvitation as Invitation,
  TextUtil,
} from '@amplify3/duplo-back-end'
import './PendingInvitationList.css'

interface PendingInvitationListProps {
  id?: string
  list: Invitation[]
}

const PendingInvitationList = (props: PendingInvitationListProps) => {
  const classNameBase = 'pending-invitation-list'
  const className = props.id ? `${classNameBase}-${props.id}` : classNameBase

  /**
   * Compare two invitations for sort.
   * @param lhs Left-hand side invitation.
   * @param rhs Right-hand side invitation.
   * @returns -1 if lhs < rhs, 0 if lhs == rhs, 1 if rhs < lhs.
   */
  function sortInvitation(lhs: Invitation, rhs: Invitation) {
    return lhs.email.localeCompare(rhs.email)
  }

  return (
    <ul className={className}>
      {props.list?.length > 0 &&
        Object.values(props.list)
          .sort((invite1: Invitation, invite2: Invitation) =>
            sortInvitation(invite1, invite2)
          )
          .map((invite, index) => (
            <PendingInvitationRow
              key={`pending-invitation-row-${index}`}
              invite={invite}
            />
          ))}
    </ul>
  )
}

const PendingInvitationRow = ({ invite }: { invite: Invitation }) => (
  <li className="pending-invitation-line">
    <div className="pending-invitation-email">{invite.email}</div>
    <div className="pending-invitation-expiration">
      expire{' '}
      {TextUtil.dateToMonthDayWithOrdinal(
        new Date(Number(invite.expiration) * 1000),
        DateType.DayMonthYear
      )}
    </div>
  </li>
)

export default PendingInvitationList
